import siteConfig from '../seo/siteConfig.cjs';

const PROD_pAIoneer_Labs_GmbH_ORIGIN = 'https://paioneer-labs.eu';

const currentBrowserOrigin = () => {
  if (typeof window === 'undefined' || !window.location?.origin) return '';
  return window.location.origin.replace(/\/+$/, '');
};

const rewriteSaasOrigin = (value) => {
  if (typeof value !== 'string' || !value.includes(PROD_pAIoneer_Labs_GmbH_ORIGIN)) return value;
  const origin = currentBrowserOrigin();
  if (!origin) return value;
  return value.replaceAll(PROD_pAIoneer_Labs_GmbH_ORIGIN, origin);
};

const rewriteSeoUrls = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => rewriteSeoUrls(item));
  }
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, entryValue]) => [key, rewriteSeoUrls(entryValue)]));
  }
  return rewriteSaasOrigin(value);
};

const setMetaTag = (attribute, key, content) => {
  if (!content || typeof document === 'undefined') return;

  let element = document.querySelector(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

const setCanonical = (url) => {
  if (!url || typeof document === 'undefined') return;

  let element = document.querySelector('link[rel="canonical"]');
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }
  element.setAttribute('href', url);
};

const clearAlternateLinks = () => {
  if (typeof document === 'undefined') return;
  document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((element) => element.remove());
};

const setAlternateLinks = (alternates) => {
  if (!Array.isArray(alternates) || typeof document === 'undefined') return;
  clearAlternateLinks();

  alternates.forEach((alternate) => {
    if (!alternate?.lang || !alternate?.href) return;
    const element = document.createElement('link');
    element.setAttribute('rel', 'alternate');
    element.setAttribute('hreflang', alternate.lang);
    element.setAttribute('href', alternate.href);
    document.head.appendChild(element);
  });
};

const setStructuredData = (data) => {
  if (!data || typeof document === 'undefined') return;

  let element = document.getElementById('page-structured-data');
  if (!element) {
    element = document.createElement('script');
    element.id = 'page-structured-data';
    element.type = 'application/ld+json';
    document.head.appendChild(element);
  }
  element.textContent = JSON.stringify(data);
};

export const applyPageSeo = (seo, currentLang) => {
  if (!seo || typeof document === 'undefined') return;

  const localizedSeo = seo.pageId
    ? siteConfig.getPageSeo(seo.pageId, currentLang)
    : {
        ...seo,
        url: seo.url?.replace('https://paioneer-labs.eu/', `https://paioneer-labs.eu/${currentLang}/`),
        canonical: seo.url?.replace('https://paioneer-labs.eu/', `https://paioneer-labs.eu/${currentLang}/`)
      };
  const normalizedSeo = rewriteSeoUrls(localizedSeo);

  if (normalizedSeo.title) document.title = normalizedSeo.title;
  if (currentLang) document.documentElement.lang = currentLang;

  setMetaTag('name', 'description', normalizedSeo.description);
  setMetaTag('name', 'keywords', normalizedSeo.keywords);
  setMetaTag('name', 'robots', normalizedSeo.robots || 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
  setMetaTag('property', 'og:title', normalizedSeo.ogTitle || normalizedSeo.title);
  setMetaTag('property', 'og:description', normalizedSeo.ogDescription || normalizedSeo.description);
  setMetaTag('property', 'og:url', normalizedSeo.url || normalizedSeo.canonical);
  setMetaTag('property', 'og:type', normalizedSeo.ogType || 'website');
  setMetaTag('property', 'og:image', normalizedSeo.ogImage || rewriteSaasOrigin(siteConfig.OG_IMAGE));
  setMetaTag('property', 'og:locale', normalizedSeo.ogLocale);
  setMetaTag('property', 'twitter:card', 'summary_large_image');
  setMetaTag('property', 'twitter:title', normalizedSeo.twitterTitle || normalizedSeo.ogTitle || normalizedSeo.title);
  setMetaTag('property', 'twitter:description', normalizedSeo.twitterDescription || normalizedSeo.ogDescription || normalizedSeo.description);
  setMetaTag('property', 'twitter:image', normalizedSeo.twitterImage || normalizedSeo.ogImage || rewriteSaasOrigin(siteConfig.OG_IMAGE));
  setCanonical(normalizedSeo.canonical || normalizedSeo.url);
  setAlternateLinks(normalizedSeo.alternates);
  setStructuredData(normalizedSeo.structuredData);
};

export const applyRouteSeo = (pathname, currentLang) => {
  if (typeof document === 'undefined') return;

  const strippedPath = pathname.replace(/^\/(de|en|nl|uk|fr|it|es|sv|fi|ja|pl|bg)(?=\/|$)/, '') || '/';
  const noIndexPath = strippedPath === '/account' || strippedPath.startsWith('/account/') || strippedPath === '/admin' || strippedPath.startsWith('/admin/');

  if (noIndexPath) {
    setMetaTag('name', 'robots', 'noindex, nofollow, noarchive');
    return;
  }

  const seo = siteConfig.getPageSeoFromPath(pathname, currentLang);
  applyPageSeo(seo, seo.language || currentLang);
};
